import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedLanguageId, EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import { useGetMarketsQuery, useMarketLanguagesQuery } from '#api/tmsApiHooks'
import { Icon } from '#components/Icon'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import WarningIcon from '#icons/warning-triangle-outline-16.svg'
import { SelectionField } from './SelectionField'
import { TextInput } from './TextInput'

export const LanguageInput: FC<{
  marketId?: EnhancedMarketId
  languageId?: EnhancedLanguageId
  onSelected: (languageId: EnhancedLanguageId | undefined) => void
  kind?: 'inline' | 'full'
  disabled?: boolean
}> = ({ marketId, languageId, onSelected, kind, disabled }) => {
  const { t } = useTranslation()
  const markets = useGetMarketsQuery()
  const isMarketsLoading = markets.isLoading || markets.isFetching
  const foundMarket = markets.data?.markets?.find((m) => m.marketId === marketId)
  const languages = useMarketLanguagesQuery({ marketId: marketId as EnhancedMarketId }, { skip: !foundMarket })

  const input = useMemo(() => {
    if (!foundMarket && !isMarketsLoading) {
      return (
        <section className="has-error">
          <Icon iconUrl={WarningIcon} />
          {!languages.isError && <span>{t('setup.language.error.select-valid-market', 'Please select a valid market')}</span>}
          {languages.isError && <span>{t('setup.language.error.failed-loading-languages', 'Failed loading languages')}</span>}
        </section>
      )
    }
    if (languages.isFetching || isMarketsLoading) {
      return (
        <section className="split-flex">
          <Spinner size="s" />
          <span>{t('setup.language.loading', 'Loading languages…')}</span>
        </section>
      )
    }

    const languageSelectItems: SelectEntry<EnhancedLanguageId>[] = [
      {
        id: 'no-language' as unknown as EnhancedLanguageId,
        value: t('setup.language.no-selection', 'No Language'),
      },
      ...(languages.data?.listOfLanguage ?? [])
        .map((l) => {
          return {
            id: l.languageID ?? ('unknown' as unknown as EnhancedLanguageId),
            value: `${l.languageName ?? t('setup.language.unknown')} (${l.languageID})`,
          } satisfies SelectEntry<EnhancedLanguageId, string>
        })
        .toSorted((a, b) => a.value.localeCompare(b.value)),
    ]
    return (
      <Select
        label={kind === 'inline' ? undefined : t('setup.language.label', 'Language')}
        options={languageSelectItems}
        value={languageId}
        placeholder={t('setup.language.unknown', 'Unknown Language')}
        onChange={(item) => {
          onSelected(item)
        }}
        slim
        disabled={disabled}
      />
    )
  }, [languages, foundMarket, isMarketsLoading, languageId, onSelected, t, kind, disabled])

  return (
    <SelectionField>
      {input}
      <TextInput
        type="number"
        value={languageId ?? ''}
        disabled={disabled}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            onSelected(x.target.value.length === 0 ? undefined : (x.target.valueAsNumber as EnhancedLanguageId))
          }
        }}
      />
    </SelectionField>
  )
}
